import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { loadStripe } from '@stripe/stripe-js';
import { Elements } from '@stripe/react-stripe-js';
import PublicLayout from './public/PublicLayout';
import StripePaymentForm from './StripePaymentForm';
import PaymentSuccessModal from './PaymentSuccessModal';

// Initialize Stripe (will load publishable key from backend)
let stripePromise = null;

const EventPaymentPage = ({ user, onLogout }) => {
    const location = useLocation();
    const navigate = useNavigate();

    // Get event details from navigation state first (needed for default payment method)
    const eventDetails = location.state || {};
    const backendPaymentMethod = eventDetails.paymentMethod;

    const [paymentMethod, setPaymentMethod] = useState(backendPaymentMethod === 'paystack' ? 'mobile' : 'card');
    const [processing, setProcessing] = useState(false);
    const [showWidget, setShowWidget] = useState(false);
    const [clientSecret, setClientSecret] = useState('');
    const [stripePublicKey, setStripePublicKey] = useState('');
    const [showSuccessModal, setShowSuccessModal] = useState(false);
    const [payerName, setPayerName] = useState('');
    const [payerEmail, setPayerEmail] = useState('');
    const [payerError, setPayerError] = useState('');

    const {
        eventId, eventName, eventDate, amount, usdDue, cpdAfricaFee, cpdAfricaCurrency, cpdAfricaMember,
        membershipIdNumber, professionalPin,
        priceType, eventFee, crceFee, certificateFee, cpdFee, cpdPoints,
        registrationId, firstName, lastName, email, requiresCpd,
        paystackLink
    } = eventDetails;
    const hasCpd = cpdFee > 0 || cpdAfricaFee > 0;

    // Backend can dictate the payment method (stripe for CRCE/Certificate/USD, paystack for CPD Africa/GHC)
    const isPaystack = backendPaymentMethod === 'paystack';
    const isStripe = backendPaymentMethod === 'stripe';
    const isLegacy = !backendPaymentMethod && amount > 0;

    // Pre-fill payer identity from registration form or logged-in user
    useEffect(() => {
        const fullName = firstName && lastName ? `${firstName} ${lastName}` : (firstName || lastName || (user ? `${user.firstName || user.first_name || ''} ${user.lastName || user.last_name || ''}`.trim() : ''));
        setPayerName(fullName || '');
        setPayerEmail(email || user?.email || '');
    }, [firstName, lastName, email, user]);

    // Load Stripe publishable key on component mount
    useEffect(() => {
        const loadStripeKey = async () => {
            try {
                const response = await fetch(`${process.env.REACT_APP_API_URL}/stripe/publishable-key`);
                const data = await response.json();
                if (data.success && data.publishableKey) {
                    setStripePublicKey(data.publishableKey);
                    stripePromise = loadStripe(data.publishableKey);
                }
            } catch (error) {
                console.error('Error loading Stripe key:', error);
            }
        };
        loadStripeKey();
    }, []);

    // Create payment intent when user selects card payment
    useEffect(() => {
        if (paymentMethod === 'card' && showWidget && (usdDue || amount) && !clientSecret) {
            createPaymentIntent();
        }
    }, [paymentMethod, showWidget, usdDue, amount]);

    const createPaymentIntent = async () => {
        // For CPD/CRCE/Certificate payers, name and email are mandatory so the admin can identify who paid
        if (hasCpd) {
            const missing = [];
            if (!payerName.trim()) missing.push('Payer Name');
            if (!payerEmail.trim()) missing.push('Payer Email');
            if (missing.length > 0) {
                setPayerError(`Required for payment: ${missing.join(', ')}`);
                setShowWidget(false);
                return;
            }
            if (!/^\S+@\S+\.\S+$/.test(payerEmail)) {
                setPayerError('Please enter a valid payer email address');
                setShowWidget(false);
                return;
            }
            setPayerError('');
        }

        try {
            const response = await fetch(`${process.env.REACT_APP_API_URL}/stripe/create-event-payment-intent`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    amount: usdDue || amount,
                    eventId,
                    eventName,
                    userId: user?.id || 'guest',
                    email: user?.email || '',
                    registrationId: registrationId || null,
                    firstName: firstName || (user ? user.firstName || user.first_name : '') || '',
                    lastName: lastName || (user ? user.lastName || user.last_name : '') || '',
                    payerEmail: payerEmail || email || user?.email || '',
                    payerName: payerName || '',
                    requiresCpd: !!hasCpd,
                    cpdPoints: cpdPoints || null,
                    cpdFee: cpdFee || 0,
                    crceFee: crceFee || 0,
                    certificateFee: certificateFee || 0
                }),
            });

            const data = await response.json();
            if (data.success && data.clientSecret) {
                setClientSecret(data.clientSecret);
            } else {
                alert('Failed to initialize payment. Please try again.');
                setShowWidget(false);
            }
        } catch (error) {
            console.error('Error creating payment intent:', error);
            alert('Failed to initialize payment. Please try again.');
            setShowWidget(false);
        }
    };

    useEffect(() => {
        // If no event details, redirect back
        if (!eventId || !amount) {
            navigate('/events');
        }
    }, [eventId, amount, navigate]);

    const handlePayment = async () => {
        setProcessing(true);
        setPayerError('');

        // Enforce payer identity for CPD payments before any payment method is chosen
        if (hasCpd) {
            const missing = [];
            if (!payerName.trim()) missing.push('Payer Name');
            if (!payerEmail.trim()) missing.push('Payer Email');
            if (missing.length > 0) {
                setPayerError(`Required for CPD payment: ${missing.join(', ')}`);
                setProcessing(false);
                return;
            }
            if (!/^\S+@\S+\.\S+$/.test(payerEmail)) {
                setPayerError('Please enter a valid payer email address');
                setProcessing(false);
                return;
            }
        }

        try {
            // Handle mobile/redirect payment (CPD Africa GHC payments)
            if (isPaystack || paymentMethod === 'mobile') {
                const redirectUrl = paystackLink || 'https://paystack.shop/pay/africaresp';
                window.open(redirectUrl, '_blank');

                alert(`You will be redirected to complete your payment.

Event: ${eventName}
Amount: GHC ${Number(cpdAfricaFee || amount).toFixed(2)}

After completing payment, please return to this page.
You will receive a confirmation email after payment.`);

                setTimeout(() => {
                    navigate('/events');
                }, 3000);

                setProcessing(false);
                return;
            }

            // Handle Card Payment - Show payment form
            if (paymentMethod === 'card') {
                setShowWidget(true);
                setProcessing(false);
                return;
            }

            // Handle Bank Transfer - Show success message
            if (paymentMethod === 'bank') {
                alert(`Thank you for choosing Bank Transfer!

Please use the bank details shown on the page to complete your payment.

Event: ${eventName}
Amount: $${amount}

After payment, please email your payment confirmation to admin@africaresp.org

You will receive a confirmation email once your payment is verified.`);

                setTimeout(() => {
                    navigate('/events');
                }, 2000);

                return;
            }

        } catch (error) {
            console.error('Payment error:', error);
            alert('Payment failed. Please try again.');
        } finally {
            setProcessing(false);
        }
    };

    if (!eventId || !amount) {
        return null; // Will redirect in useEffect
    }

    return (
        <PublicLayout>
            <div className="bg-light py-5">
                <div className="container">
                    <div className="row justify-content-center">
                        <div className="col-lg-8">
                            {/* Page Header */}
                            <div className="mb-4">
                                <button
                                    onClick={() => navigate('/events')}
                                    className="btn btn-outline-secondary btn-sm mb-3"
                                >
                                    <i className="fas fa-arrow-left me-2"></i>
                                    Back to Events
                                </button>
                                <h1 className="h3 fw-bold text-dark">Event Registration Payment</h1>
                                <p className="text-muted">Complete payment for your event registration</p>
                            </div>

                            <div className="row g-4">
                                {/* Order Summary */}
                                <div className="col-lg-5">
                                    <div className="card border-0 shadow-sm sticky-top" style={{ top: '20px' }}>
                                        <div className="card-header bg-white border-bottom">
                                            <h5 className="mb-0 fw-bold">Registration Summary</h5>
                                        </div>
                                        <div className="card-body">
                                            <div className="mb-3">
                                                <h6 className="fw-bold text-dark">{eventName}</h6>
                                                <p className="text-muted small mb-0">
                                                    <i className="fas fa-calendar me-2"></i>
                                                    {new Date(eventDate).toLocaleDateString('en-US', {
                                                        weekday: 'long',
                                                        year: 'numeric',
                                                        month: 'long',
                                                        day: 'numeric'
                                                    })}
                                                </p>
                                                <p className="text-muted small mb-0">
                                                    <i className="fas fa-tag me-2"></i>
                                                    {priceType} Registration
                                                </p>
                                            </div>

                                            <hr />

                                            <div className="d-flex justify-content-between mb-2">
                                                <span className="text-muted">Registration Fee:</span>
                                                <span className="fw-medium">{eventFee > 0 ? `$${Number(eventFee || 0).toFixed(2)}` : 'Free'}</span>
                                            </div>

                                            {crceFee > 0 && (
                                                <div className="d-flex justify-content-between mb-2">
                                                    <span className="text-muted">CRCE Points (1.5 points per webinar):</span>
                                                    <span className="fw-medium">${Number(crceFee).toFixed(2)}</span>
                                                </div>
                                            )}

                                            {cpdAfricaFee > 0 && (
                                                <div className="d-flex justify-content-between mb-2">
                                                    <span className="text-muted">CPD Africa Points (20 points):</span>
                                                    <span className="fw-medium">GHC {Number(cpdAfricaFee).toFixed(2)}</span>
                                                </div>
                                            )}

                                            {cpdAfricaFee > 0 && (
                                                <div className="small mb-2" style={{ color: cpdAfricaMember ? '#6B8E23' : '#666' }}>
                                                    <i className={`fas ${cpdAfricaMember ? 'fa-check-circle' : 'fa-user'} me-2`}></i>
                                                    {cpdAfricaMember ? 'ARCA Member pricing' : 'Non-member pricing'}
                                                    {membershipIdNumber && ` — ID: ${membershipIdNumber}`}
                                                </div>
                                            )}

                                            {cpdFee > 0 && (
                                                <div className="d-flex justify-content-between mb-2">
                                                    <span className="text-muted">CPD Fee{cpdPoints ? ` (${cpdPoints} Points + Certificate)` : ''}:</span>
                                                    <span className="fw-medium">${Number(cpdFee).toFixed(2)}</span>
                                                </div>
                                            )}

                                            {certificateFee > 0 && (
                                                <div className="d-flex justify-content-between mb-2">
                                                    <span className="text-muted">Certificate:</span>
                                                    <span className="fw-medium">${Number(certificateFee).toFixed(2)}</span>
                                                </div>
                                            )}

                                            <hr />

                                            <div className="d-flex justify-content-between mb-3">
                                                <span className="fw-bold h5 mb-0">Total:</span>
                                                <span className="fw-bold h5 mb-0" style={{ color: '#6B8E23' }}>
                                                    {isPaystack
                                                        ? `GHC ${Number(cpdAfricaFee || 0).toFixed(2)}`
                                                        : `$${Number(usdDue || amount).toFixed(2)}`}
                                                </span>
                                            </div>

                                            <div className="alert alert-info small mb-0">
                                                <i className="fas fa-info-circle me-2"></i>
                                                You will receive a confirmation email after payment is processed.
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                {/* Payment Form */}
                                <div className="col-lg-7">
                                    <div className="card border-0 shadow-sm">
                                        <div className="card-header bg-white border-bottom">
                                            <h5 className="mb-0 fw-bold">Payment Method</h5>
                                        </div>
                                        <div className="card-body p-4">
                                            {!showWidget ? (
                                                <>
                                                    {/* CPD Payer Identity - required so admin can identify who paid */}
                                                    {hasCpd && (
                                                        <div className="alert alert-warning border-warning mb-4">
                                                            <h6 className="fw-bold mb-2">
                                                                <i className="fas fa-id-card me-2"></i>
                                                                CPD Payment Identification Required
                                                            </h6>
                                                            <p className="small mb-3">
                                                                Because you selected CPD points/certificate, we need your name and email to match your payment with your registration.
                                                            </p>
                                                            <div className="row g-3">
                                                                <div className="col-md-6">
                                                                    <label className="form-label fw-bold small">Payer Name *</label>
                                                                    <input
                                                                        type="text"
                                                                        className="form-control"
                                                                        value={payerName}
                                                                        onChange={(e) => setPayerName(e.target.value)}
                                                                        placeholder="Full name on payment"
                                                                    />
                                                                </div>
                                                                <div className="col-md-6">
                                                                    <label className="form-label fw-bold small">Payer Email *</label>
                                                                    <input
                                                                        type="email"
                                                                        className="form-control"
                                                                        value={payerEmail}
                                                                        onChange={(e) => setPayerEmail(e.target.value)}
                                                                        placeholder="Email for payment confirmation"
                                                                    />
                                                                </div>
                                                            </div>
                                                        </div>
                                                    )}

                                                    {payerError && (
                                                        <div className="alert alert-danger" role="alert">
                                                            <i className="fas fa-exclamation-circle me-2"></i>
                                                            {payerError}
                                                        </div>
                                                    )}

                                                    {/* Payment Method Selection */}
                                                    <div className="mb-4">
                                                        <label className="form-label fw-bold">Select Payment Method</label>

                                                        {/* Card Payment (Visa/Mastercard/AmEx) */}
                                                        {!isPaystack && (
                                                            <div
                                                                className={`card mb-3 cursor-pointer ${paymentMethod === 'card' ? 'border-success border-2' : ''}`}
                                                                style={{ cursor: 'pointer' }}
                                                                onClick={() => setPaymentMethod('card')}
                                                            >
                                                                <div className="card-body d-flex align-items-center">
                                                                    <input
                                                                        type="radio"
                                                                        name="payment"
                                                                        checked={paymentMethod === 'card'}
                                                                        onChange={() => setPaymentMethod('card')}
                                                                        className="form-check-input me-3"
                                                                    />
                                                                    <div className="flex-grow-1">
                                                                        <h6 className="mb-0 fw-bold">Credit/Debit Card</h6>
                                                                        <small className="text-muted">Visa, Mastercard, American Express</small>
                                                                    </div>
                                                                    <div className="d-flex gap-2">
                                                                        <i className="fab fa-cc-visa fa-2x text-primary"></i>
                                                                        <i className="fab fa-cc-mastercard fa-2x" style={{ color: '#eb001b' }}></i>
                                                                        <i className="fab fa-cc-amex fa-2x" style={{ color: '#006fcf' }}></i>
                                                                    </div>
                                                                </div>
                                                            </div>
                                                        )}

                                                        {/* Mobile Money / Online Payment */}
                                                        {(isPaystack || isLegacy) && (
                                                            <div
                                                                className={`card mb-3 cursor-pointer ${paymentMethod === 'mobile' ? 'border-success border-2' : ''}`}
                                                                style={{ cursor: 'pointer' }}
                                                                onClick={() => setPaymentMethod('mobile')}
                                                            >
                                                                <div className="card-body d-flex align-items-center">
                                                                    <input
                                                                        type="radio"
                                                                        name="payment"
                                                                        checked={paymentMethod === 'mobile'}
                                                                        onChange={() => setPaymentMethod('mobile')}
                                                                        className="form-check-input me-3"
                                                                    />
                                                                    <div className="flex-grow-1">
                                                                        <h6 className="mb-0 fw-bold">Mobile Money</h6>
                                                                        <small className="text-muted">Mobile Money, Card, or Bank Transfer</small>
                                                                    </div>
                                                                    <i className="fas fa-mobile-alt fa-2x" style={{ color: '#6B8E23' }}></i>
                                                                </div>
                                                            </div>
                                                        )}

                                                        {/* Bank Transfer */}
                                                        {!isPaystack && (
                                                            <div
                                                                className={`card mb-3 cursor-pointer ${paymentMethod === 'bank' ? 'border-success border-2' : ''}`}
                                                                style={{ cursor: 'pointer' }}
                                                                onClick={() => setPaymentMethod('bank')}
                                                            >
                                                                <div className="card-body d-flex align-items-center">
                                                                    <input
                                                                        type="radio"
                                                                        name="payment"
                                                                        checked={paymentMethod === 'bank'}
                                                                        onChange={() => setPaymentMethod('bank')}
                                                                        className="form-check-input me-3"
                                                                    />
                                                                    <div className="flex-grow-1">
                                                                        <h6 className="mb-0 fw-bold">Bank Transfer</h6>
                                                                        <small className="text-muted">Direct bank transfer (International & Local)</small>
                                                                    </div>
                                                                    <i className="fas fa-university fa-2x" style={{ color: '#6B8E23' }}></i>
                                                                </div>
                                                            </div>
                                                        )}
                                                    </div>

                                                    {/* Payment Instructions */}
                                                    {paymentMethod === 'card' && (
                                                        <div className="alert alert-light">
                                                            <h6 className="fw-bold mb-2">Credit/Debit Card Payment</h6>
                                                            <p className="small mb-2">Complete your payment securely below using our embedded payment widget.</p>
                                                            <p className="small mb-0 text-muted">Accepts Visa, Mastercard, and American Express.</p>
                                                        </div>
                                                    )}

                                                    {paymentMethod === 'mobile' && (
                                                        <div className="alert alert-light">
                                                            <h6 className="fw-bold mb-2">Mobile Money Payment</h6>
                                                            <p className="small mb-2">
                                                                Click the button below to pay using Mobile Money, Card, or Bank Transfer.
                                                            </p>
                                                            <p className="small mb-0 text-muted">You'll be redirected to complete your payment.</p>
                                                        </div>
                                                    )}

                                                    {paymentMethod === 'bank' && (
                                                        <div className="alert alert-light">
                                                            <h6 className="fw-bold mb-3">Bank Transfer Details</h6>

                                                            {/* International Bank */}
                                                            <div className="mb-3 p-3 bg-white border rounded">
                                                                <h6 className="fw-bold text-primary mb-2"><i className="fas fa-globe me-2"></i>International Transfer</h6>
                                                                <div className="small">
                                                                    <div className="row mb-1">
                                                                        <div className="col-4 text-muted">Bank Name:</div>
                                                                        <div className="col-8 fw-medium">Bank of America</div>
                                                                    </div>
                                                                    <div className="row mb-1">
                                                                        <div className="col-4 text-muted">Account Name:</div>
                                                                        <div className="col-8 fw-medium">Africa Respiratory Care Association Corp.</div>
                                                                    </div>
                                                                    <div className="row mb-1">
                                                                        <div className="col-4 text-muted">Account No:</div>
                                                                        <div className="col-8 fw-bold text-dark">334081495079</div>
                                                                    </div>
                                                                    <div className="row mb-1">
                                                                        <div className="col-4 text-muted">Routing:</div>
                                                                        <div className="col-8 fw-bold text-dark">026009593</div>
                                                                    </div>
                                                                    <div className="row mb-1">
                                                                        <div className="col-4 text-muted">SWIFT:</div>
                                                                        <div className="col-8 fw-bold text-dark">BOFAUS6S</div>
                                                                    </div>
                                                                    <div className="row">
                                                                        <div className="col-4 text-muted">Address:</div>
                                                                        <div className="col-8">Bank of America, N.A. 555 California St., San Francisco, CA 94104</div>
                                                                    </div>
                                                                </div>
                                                            </div>

                                                            {/* Local Bank (Africa) */}
                                                            <div className="p-3 bg-white border rounded">
                                                                <h6 className="fw-bold text-success mb-2"><i className="fas fa-map-marker-alt me-2"></i>Local Transfer (Africa)</h6>
                                                                <div className="small">
                                                                    <div className="row mb-1">
                                                                        <div className="col-4 text-muted">Bank Name:</div>
                                                                        <div className="col-8 fw-medium">Stanbic Bank Ghana</div>
                                                                    </div>
                                                                    <div className="row mb-1">
                                                                        <div className="col-4 text-muted">Account Name:</div>
                                                                        <div className="col-8 fw-medium">Africa Respiratory Care Association LBG</div>
                                                                    </div>
                                                                    <div className="row mb-1">
                                                                        <div className="col-4 text-muted">Account No:</div>
                                                                        <div className="col-8 fw-bold text-dark">9040012558435</div>
                                                                    </div>
                                                                    <div className="row mb-1">
                                                                        <div className="col-4 text-muted">Branch:</div>
                                                                        <div className="col-8 fw-medium">Graphic Road</div>
                                                                    </div>
                                                                    <div className="row mb-1">
                                                                        <div className="col-4 text-muted">SWIFT:</div>
                                                                        <div className="col-8 fw-bold text-dark">SBICGHAC</div>
                                                                    </div>
                                                                    <div className="row">
                                                                        <div className="col-4 text-muted">Address:</div>
                                                                        <div className="col-8">Stanbic Heights, No. 215 South Liberation Link, Airport City-Accra Ghana</div>
                                                                    </div>
                                                                </div>
                                                            </div>

                                                            <div className="alert alert-warning mt-3 mb-0">
                                                                <small><i className="fas fa-info-circle me-2"></i>After completing the bank transfer, please email your payment confirmation to <strong>admin@africaresp.org</strong> with your event details.</small>
                                                            </div>
                                                        </div>
                                                    )}

                                                    {/* Payment Button */}
                                                    <button
                                                        className="btn btn-lg w-100 text-white fw-bold mt-3"
                                                        style={{ backgroundColor: '#6B8E23', border: 'none' }}
                                                        onClick={handlePayment}
                                                        disabled={processing}
                                                    >
                                                        {processing ? (
                                                            <>
                                                                <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                                                                Processing...
                                                            </>
                                                        ) : (
                                                            <>
                                                                <i className="fas fa-lock me-2"></i>
                                                                Complete Payment {isPaystack ? `GHC ${Number(cpdAfricaFee || 0).toFixed(2)}` : `$${Number(usdDue || amount).toFixed(2)}`}
                                                            </>
                                                        )}
                                                    </button>

                                                    <div className="text-center mt-3">
                                                        <small className="text-muted">
                                                            <i className="fas fa-shield-alt me-1"></i>
                                                            Secure payment powered by ARCA
                                                        </small>
                                                    </div>
                                                </>
                                            ) : (
                                                <>
                                                    {/* Stripe Payment Form */}
                                                    <div className="mb-3">
                                                        <button
                                                            onClick={() => {
                                                                setShowWidget(false);
                                                                setClientSecret('');
                                                            }}
                                                            className="btn btn-sm btn-outline-secondary mb-3"
                                                        >
                                                            <i className="fas fa-arrow-left me-2"></i>
                                                            Back to Payment Methods
                                                        </button>
                                                    </div>

                                                    <div className="alert alert-info mb-3">
                                                        <i className="fas fa-info-circle me-2"></i>
                                                        Complete your payment securely below. You will receive a confirmation email after payment.
                                                    </div>

                                                    {/* Stripe Elements */}
                                                    {clientSecret && stripePublicKey && stripePromise ? (
                                                        <Elements stripe={stripePromise} options={{ clientSecret }}>
                                                            <StripePaymentForm
                                                                amount={usdDue || amount}
                                                                onSuccess={() => {
                                                                    setShowSuccessModal(true);
                                                                }}
                                                                onError={(error) => {
                                                                    console.error('Payment error:', error);
                                                                }}
                                                            />
                                                        </Elements>
                                                    ) : (
                                                        <div className="text-center py-5">
                                                            <div className="spinner-border text-primary" role="status" style={{ width: '3rem', height: '3rem' }}>
                                                                <span className="visually-hidden">Loading payment form...</span>
                                                            </div>
                                                            <p className="mt-3 text-muted">Initializing secure payment...</p>
                                                        </div>
                                                    )}
                                                </>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Payment Success Modal */}
            <PaymentSuccessModal
                show={showSuccessModal}
                onClose={() => setShowSuccessModal(false)}
                paymentType="event"
                details={{
                    name: eventName,
                    date: eventDate,
                    amount: amount
                }}
            />
        </PublicLayout>
    );
};

export default EventPaymentPage;
